/**
 * EmployeeList.js — PeoplesDock
 *
 * FEATURE 10 (this version) — Letter Generation integration.
 *   NEW: GenerateLetterModal component — lets HR generate an
 *   Appointment Letter, Experience Certificate, or Salary Revision
 *   Letter for any employee directly from this list. A new "Letters"
 *   icon button (FileSignature) appears in the Actions column,
 *   admin-only, alongside the existing View/Download/Edit/Hold/Delete
 *   buttons. Calls POST /letters/generate with responseType: 'blob'
 *   (same pattern already used by downloadPayslip/downloadTemplate
 *   in this file) and triggers an immediate browser download of the
 *   generated PDF.
 *
 *   Offer Letters are intentionally NOT included here — those target
 *   ATS candidates (not employees) and belong in the ATS candidate
 *   detail page instead.
 *
 * ADDED (earlier): Employee PAN Number field
 *   - Added to EMPTY_FORM
 *   - Added to edit form pre-fill (isEdit branch)
 *   - Added input field in "Payroll & Tax Settings" section of EmployeeFormModal
 *   - Added PAN validation in validateForm (format: ABCDE1234F)
 *   - Added read-only display in ViewModal "Payroll & Tax Settings" section
 *   - Saved via existing PUT /employees/:id — no backend change needed
 *
 * EXISTING: CSV Import 3-step preview flow, all other features unchanged.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Table, Button, Row, Col, Form, Card, Badge, Spinner,
    InputGroup, Modal, Alert,
} from 'react-bootstrap';
import {
    Search, UserPlus, Eye, Edit2, Trash2,
    PauseCircle, PlayCircle, Download, Upload,
    Phone, Mail, User, Briefcase, Building2,
    Calendar, IndianRupee, GitBranch, Clock, Lock,
    MapPin, Shield, ToggleLeft, FileSpreadsheet,
    CheckCircle, XCircle, AlertTriangle, ChevronRight,
    FileSignature,
} from 'lucide-react';
import { toast } from 'react-toastify';
import axiosInstance from '../../api/axiosConfig';
import { useAuth } from '../../context/AuthContext';
const INDIAN_STATES = [
    'Andhra Pradesh','Arunachal Pradesh','Assam','Bihar','Chhattisgarh',
    'Delhi','Goa','Gujarat','Haryana','Himachal Pradesh',
    'Jammu and Kashmir','Jharkhand','Karnataka','Kerala','Madhya Pradesh',
    'Maharashtra','Manipur','Meghalaya','Mizoram','Nagaland','Odisha',
    'Punjab','Rajasthan','Sikkim','Tamil Nadu','Telangana','Tripura',
    'Uttar Pradesh','Uttarakhand','West Bengal',
];
const fmtPhone = (v = '') => {
    const d = String(v).replace(/\D/g, '');
    if (d.length === 10) return `+91 ${d.slice(0,5)}-${d.slice(5)}`;
    if (d.length === 12) return `+${d.slice(0,2)} ${d.slice(2,7)}-${d.slice(7)}`;
    return v || '—';
};
const fmtCurrency = (n) =>
    new Intl.NumberFormat('en-IN', {
        style: 'currency', currency: 'INR', maximumFractionDigits: 0,
    }).format(parseFloat(n) || 0);
const fmtDate = (d) => {
    if (!d) return '—';
    return new Date(String(d).slice(0, 10) + 'T00:00:00')
        .toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};
const calcAge = (dob) => {
    if (!dob) return null;
    const birth = new Date(String(dob).slice(0, 10) + 'T00:00:00');
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const m = today.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
    return age >= 0 ? age : null;
};
const ADMIN_ROLES = ['admin','super-admin','hr-manager','account-manager'];
// FEATURE 10: letter types available from the employee list (Offer is
// candidate-facing via ATS, not included here)
const LETTER_TYPE_OPTIONS = [
    { value: 'appointment',     label: 'Appointment Letter' },
    { value: 'experience',      label: 'Experience Certificate' },
    { value: 'salary_revision', label: 'Salary Revision Letter' },
];
// --- Confirm Modal ----------------------------------------------------------
const ConfirmModal = ({ show, onHide, onConfirm, loading, title, message, confirmLabel, variant = 'danger' }) => (
    <Modal show={show} onHide={onHide} centered size="sm">
        <Modal.Header closeButton className="border-0">
            <Modal.Title className="fw-bold fs-6">{title}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center py-3">
            <p className="text-muted mb-0" style={{ fontSize: 14 }}>{message}</p>
        </Modal.Body>
        <Modal.Footer className="border-0 justify-content-center gap-2">
            <Button variant="light" onClick={onHide} disabled={loading}>No, Cancel</Button>
            <Button variant={variant} onClick={onConfirm} disabled={loading}>
                {loading && <Spinner size="sm" className="me-1"/>}{confirmLabel}
            </Button>
        </Modal.Footer>
    </Modal>
);
// ============================================================================
// FEATURE 10: GENERATE LETTER MODAL
// ============================================================================
const GenerateLetterModal = ({ show, onHide, employee }) => {
    const [letterType,  setLetterType]  = useState('');
    const [extraFields, setExtraFields] = useState({});
    const [generating,  setGenerating]  = useState(false);
    // FEATURE 10 fix: Salary Revision Letters now require an APPROVED
    // arrears record — no more free-text salary entry. This guarantees
    // the letter can never drift from what was actually approved in the
    // Salary Arrears workflow (and matches the real salary change, which
    // now only happens at arrears-approval time — see
    // payroll-arrears.routes.js POST /:id/approve).
    const [approvedArrears,  setApprovedArrears]  = useState([]);
    const [loadingArrears,   setLoadingArrears]    = useState(false);
    const [selectedArrearsId, setSelectedArrearsId] = useState('');
    useEffect(() => {
        if (show) {
            setLetterType('');
            setExtraFields({});
            setApprovedArrears([]);
            setSelectedArrearsId('');
        }
    }, [show]);
    useEffect(() => {
        if (letterType !== 'salary_revision' || !employee) return;
        setLoadingArrears(true);
        axiosInstance.get('/payroll/arrears', {
            params: { employee_id: employee.id, status: 'approved' },
        })
            .then(r => setApprovedArrears(r.data.data || []))
            .catch(() => setApprovedArrears([]))
            .finally(() => setLoadingArrears(false));
    }, [letterType, employee]);
    const setField = (k, v) => setExtraFields(p => ({ ...p, [k]: v }));
    const selectArrears = (id) => {
        setSelectedArrearsId(id);
        const rec = approvedArrears.find(a => String(a.id) === String(id));
        if (rec) {
            setField('new_salary', rec.new_salary);
            setField('effective_date', String(rec.effective_date).slice(0, 10));
            setField('old_salary', rec.old_salary);
        }
    };
    const isValid = () => {
        if (!letterType) return false;
        if (letterType === 'salary_revision')
            return !!selectedArrearsId; // must pick an approved record
        return true; // appointment / experience need no required extra fields
    };
    const generate = async () => {
        if (!isValid()) {
            toast.error('Please fill in the required fields');
            return;
        }
        setGenerating(true);
        try {
            const res = await axiosInstance.post('/letters/generate', {
                letter_type: letterType,
                target_type: 'employee',
                target_id:   employee.id,
                extra_fields: extraFields,
            }, { responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
            const a   = document.createElement('a');
            a.href     = url;
            a.download = `${letterType}_${employee.first_name}_${employee.last_name}.pdf`;
            a.click();
            window.URL.revokeObjectURL(url);
            toast.success('Letter generated and downloaded');
            onHide();
        } catch (err) {
            let message = 'Failed to generate letter';
            try {
                if (err.response?.data instanceof Blob) {
                    const text = await err.response.data.text();
                    const parsed = JSON.parse(text);
                    message = parsed.message || message;
                } else {
                    message = err.response?.data?.message || message;
                }
            } catch {}
            toast.error(message);
        } finally {
            setGenerating(false);
        }
    };
    if (!employee) return null;
    return (
        <Modal show={show} onHide={onHide} centered>
            <Modal.Header closeButton className="border-0">
                <Modal.Title className="fw-bold fs-6">
                    <FileSignature size={18} className="me-2 text-primary"/>
                    Generate Letter — {employee.first_name} {employee.last_name}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form.Group className="mb-3">
                    <Form.Label className="small fw-bold">Letter Type *</Form.Label>
                    <Form.Select value={letterType} onChange={e => setLetterType(e.target.value)}>
                        <option value="">Select letter type…</option>
                        {LETTER_TYPE_OPTIONS.map(o => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                    </Form.Select>
                </Form.Group>
                {letterType === 'appointment' && (
                    <Alert variant="info" className="small py-2 mb-0">
                        All details (designation, department, join date, salary)
                        will be pulled automatically from this employee's record.
                    </Alert>
                )}
                {letterType === 'experience' && (
                    <>
                        <Form.Group className="mb-2">
                            <Form.Label className="small fw-bold">
                                Last Working Day <span className="text-muted fw-normal">(optional — defaults to today)</span>
                            </Form.Label>
                            <Form.Control type="date"
                                value={extraFields.last_working_day || ''}
                                onChange={e => setField('last_working_day', e.target.value)}/>
                        </Form.Group>
                        <Alert variant="info" className="small py-2 mb-0">
                            Years of service will be calculated automatically from
                            join date to the last working day above.
                        </Alert>
                    </>
                )}
                {/* FEATURE 10 fix: salary_revision now requires picking an
                    APPROVED arrears record — no more free-text salary entry */}
                {letterType === 'salary_revision' && (
                    <>
                        {loadingArrears ? (
                            <div className="text-center py-3"><Spinner size="sm"/></div>
                        ) : approvedArrears.length === 0 ? (
                            <Alert variant="warning" className="small py-2 mb-0">
                                This employee has no <strong>approved</strong> salary revision in
                                Payroll → Salary Arrears yet. Please calculate and approve a
                                revision there first — the letter is generated from an approved
                                record so it can never differ from what was actually approved.
                            </Alert>
                        ) : (
                            <>
                                <Form.Group className="mb-3">
                                    <Form.Label className="small fw-bold">
                                        Approved Salary Revision *
                                    </Form.Label>
                                    <Form.Select value={selectedArrearsId}
                                        onChange={e => selectArrears(e.target.value)}>
                                        <option value="">Select an approved revision…</option>
                                        {approvedArrears.map(a => (
                                            <option key={a.id} value={a.id}>
                                                {fmtCurrency(a.old_salary)} → {fmtCurrency(a.new_salary)}
                                                {' '}(effective {String(a.effective_date).slice(0,10)})
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                                {selectedArrearsId && (
                                    <Alert variant="success" className="small py-2 mb-0">
                                        This letter will reflect the approved revision exactly:
                                        previous salary <strong>{fmtCurrency(extraFields.old_salary)}</strong> →
                                        new salary <strong>{fmtCurrency(extraFields.new_salary)}</strong>,
                                        effective <strong>{extraFields.effective_date}</strong>.
                                    </Alert>
                                )}
                            </>
                        )}
                    </>
                )}
                <div className="d-flex gap-2 mt-4">
                    <Button variant="light" className="flex-grow-1" onClick={onHide}>Cancel</Button>
                    <Button variant="primary" className="flex-grow-1 fw-bold"
                        disabled={!isValid() || generating} onClick={generate}>
                        {generating
                            ? <><Spinner size="sm" className="me-2"/>Generating…</>
                            : <><Download size={14} className="me-1"/>Generate &amp; Download</>}
                    </Button>
                </div>
            </Modal.Body>
        </Modal>
    );
};
// --- View Modal --------------------------------------------------------------
const ViewModal = ({ show, onHide, emp }) => {
    if (!emp) return null;
    const Section = ({ title, children }) => (
        <div className="mb-4">
            <div className="small fw-bold text-muted text-uppercase mb-3"
                style={{ letterSpacing: '0.06em', borderBottom: '1px solid #f1f5f9', paddingBottom: 6 }}>
                {title}
            </div>
            <Row className="g-3">{children}</Row>
        </div>
    );
    const Field = ({ label, value, icon: Icon }) => (
        <Col xs={12} sm={6}>
            <div className="small text-muted fw-bold mb-1">{label}</div>
            <div className="fw-medium d-flex align-items-center gap-1">
                {Icon && <Icon size={14} className="text-muted flex-shrink-0"/>}
                <span>{value || '—'}</span>
            </div>
        </Col>
    );
    const age = calcAge(emp.dob);
    return (
        <Modal show={show} onHide={onHide} centered size="lg">
            <Modal.Header closeButton className="border-0">
                <Modal.Title className="fw-bold">Employee Profile</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="d-flex align-items-center gap-3 p-3 rounded-3 mb-4" style={{ background: '#f8fafc' }}>
                    <div style={{ width:56, height:56, borderRadius:'50%', flexShrink:0, background:'#eef2ff', color:'#4f46e5', display:'flex', alignItems:'center', justifyContent:'center', fontSize:22, fontWeight:700 }}>
                        {emp.first_name?.[0]}{emp.last_name?.[0]}
                    </div>
                    <div>
                        <h5 className="fw-bold mb-1">{emp.first_name} {emp.last_name}</h5>
                        <div className="d-flex gap-2 flex-wrap">
                            <Badge bg={emp.status === 'active' ? 'success' : 'warning'} className="text-capitalize">
                                {emp.status === 'active' ? 'Active' : 'On Hold'}
                            </Badge>
                            {emp.role_name && <Badge bg="light" text="dark" style={{ fontSize:11 }}>{emp.role_name}</Badge>}
                            {!!emp.pf_opt_out && <Badge bg="warning" text="dark" style={{ fontSize:10 }}>PF Exempt</Badge>}
                        </div>
                    </div>
                </div>
                <Section title="Personal Details">
                    <Field label="Full Name"     value={`${emp.first_name} ${emp.last_name}`} icon={User}/>
                    <Field label="Employee Code" value={emp.employee_code || `PD-${emp.id}`}  icon={Building2}/>
                    <Field label="Email Address" value={emp.email}                             icon={Mail}/>
                    <Field label="Phone Number"  value={fmtPhone(emp.phone)}                  icon={Phone}/>
                    <Field label="Date of Birth"
                        value={emp.dob ? `${fmtDate(emp.dob)}${age !== null ? ` (${age} years old)` : ''}` : '—'}
                        icon={Calendar}/>
                </Section>
                <Section title="Organisation">
                    <Field label="Department"        value={emp.department_name}  icon={Building2}/>
                    <Field label="Designation"       value={emp.designation_name} icon={Briefcase}/>
                    <Field label="Reporting Manager" value={emp.manager_name}     icon={GitBranch}/>
                    <Field label="Shift"             value={emp.shift_name}       icon={Clock}/>
                    <Field label="Location"          value={emp.location_name}    icon={MapPin}/>
                </Section>
                <Section title="Employment Details">
                    <Field label="Employment Type" value={emp.employment_type || 'Full-Time'}/>
                    <Field label="Join Date" value={emp.join_date ? fmtDate(emp.join_date) : '—'} icon={Calendar}/>
                    <Field label="Gross Salary (₹)" value={fmtCurrency(emp.gross_salary || emp.basic_salary)} icon={IndianRupee}/>
                </Section>
                <Section title="Payroll & Tax Settings">
                    <Field label="Work State (PT)" value={emp.work_state || '— (Company default)'} icon={MapPin}/>
                    <Field label="Income Tax Regime"
                        value={emp.income_tax_regime === 'old' ? 'Old Regime' : 'New Regime (FY 2024-25 default)'}
                        icon={Shield}/>
                    <Field label="Provident Fund (PF)"
                        value={emp.pf_opt_out ? 'Opted Out — PF not deducted' : 'Active — PF deducted as per company mode'}
                        icon={ToggleLeft}/>
                    <Field
                        label="PAN Number"
                        value={emp.pan_number
                            ? emp.pan_number
                            : '⚠ Not provided — required for Form 16'}
                        icon={Shield}/>
                </Section>
            </Modal.Body>
            <Modal.Footer className="border-0">
                <Button variant="light" onClick={onHide}>Close</Button>
            </Modal.Footer>
        </Modal>
    );
};
// ============================================================================
// IMPORT PREVIEW MODAL
// ============================================================================
const ImportPreviewModal = ({ show, onHide, previewData, onConfirm, importing }) => {
    const [rows, setRows] = useState([]);
    useEffect(() => {
        if (previewData?.rows) setRows(previewData.rows.map(r => ({ ...r })));
    }, [previewData]);
    if (!previewData) return null;
    const { summary = {} } = previewData;
    const includedCount = rows.filter(r => r.include !== false && r.status !== 'duplicate').length;
    const toggleRow = (idx) => {
        setRows(prev => prev.map((r, i) => i === idx ? { ...r, include: !r.include } : r));
    };
    const editField = (idx, field, value) => {
        setRows(prev => prev.map((r, i) => {
            if (i !== idx) return r;
            const updated = { ...r, [field]: value };
            const errors = [];
            if (!updated.first_name?.trim()) errors.push('first_name required');
            if (!updated.last_name?.trim())  errors.push('last_name required');
            if (!updated.email?.trim())      errors.push('email required');
            else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(updated.email.trim())) errors.push('invalid email');
            updated.errors  = errors;
            updated.status  = updated.status === 'duplicate' ? 'duplicate' : (errors.length ? 'error' : 'ready');
            updated.include = updated.status === 'ready';
            return updated;
        }));
    };
    const rowBg = (row) => {
        if (row.status === 'duplicate') return '#fffbeb';
        if (row.status === 'error')     return '#fef2f2';
        if (row.include === false)      return '#f8fafc';
        return '#f0fdf4';
    };
    const rowIcon = (row) => {
        if (row.status === 'duplicate') return <AlertTriangle size={14} color="#d97706" />;
        if (row.status === 'error')     return <XCircle size={14} color="#dc2626" />;
        if (row.include === false)      return <XCircle size={14} color="#9ca3af" />;
        return <CheckCircle size={14} color="#059669" />;
    };
    return (
        <Modal show={show} onHide={onHide} centered size="xl" backdrop="static">
            <Modal.Header closeButton className="border-0">
                <div>
                    <Modal.Title className="fw-bold fs-5">
                        <FileSpreadsheet size={18} className="me-2 text-primary" />
                        Review Before Import
                    </Modal.Title>
                    <div className="text-muted small mt-1">
                        Review and edit each row. Uncheck rows you want to skip.
                        Click <strong>Import</strong> when ready.
                    </div>
                </div>
            </Modal.Header>
            <Modal.Body className="p-0">
                <div className="d-flex gap-3 px-4 py-3 border-bottom" style={{ background: '#f8fafc' }}>
                    {[
                        { label: 'Total rows',  count: summary.total,      color: '#374151', bg: '#e5e7eb' },
                        { label: 'Ready',       count: summary.ready,      color: '#059669', bg: '#d1fae5' },
                        { label: 'Errors',      count: summary.withErrors, color: '#dc2626', bg: '#fee2e2' },
                        { label: 'Duplicates',  count: summary.duplicates, color: '#d97706', bg: '#fef3c7' },
                        { label: 'Will import', count: includedCount,      color: '#4f46e5', bg: '#e0e7ff' },
                    ].map(({ label, count, color, bg }) => (
                        <div key={label} className="text-center px-3 py-2 rounded"
                            style={{ background: bg, minWidth: 90 }}>
                            <div style={{ fontSize: 22, fontWeight: 800, color }}>{count ?? 0}</div>
                            <div style={{ fontSize: 11, color, fontWeight: 600 }}>{label}</div>
                        </div>
                    ))}
                </div>
                <div className="d-flex gap-3 px-4 py-2 border-bottom" style={{ fontSize: 11, color: '#6b7280' }}>
                    <span><CheckCircle size={12} color="#059669" className="me-1" />Ready to import</span>
                    <span><XCircle size={12} color="#dc2626" className="me-1" />Has errors — fix to include</span>
                    <span><AlertTriangle size={12} color="#d97706" className="me-1" />Duplicate email — will be skipped</span>
                    <span><XCircle size={12} color="#9ca3af" className="me-1" />Manually excluded</span>
                </div>
                <div style={{ maxHeight: 420, overflowY: 'auto', overflowX: 'auto' }}>
                    <table className="table table-sm mb-0 align-middle" style={{ fontSize: 12, minWidth: 900 }}>
                        <thead style={{ position: 'sticky', top: 0, background: '#f1f5f9', zIndex: 1 }}>
                            <tr className="text-muted text-uppercase" style={{ fontSize: 10 }}>
                                <th className="ps-3" style={{ width: 40 }}>
                                    <Form.Check
                                        type="checkbox"
                                        checked={rows.filter(r => r.status !== 'duplicate' && r.status !== 'error').every(r => r.include !== false)}
                                        onChange={e => setRows(prev => prev.map(r =>
                                            r.status === 'duplicate' || r.status === 'error'
                                                ? r
                                                : { ...r, include: e.target.checked }
                                        ))}
                                        title="Select all ready rows"
                                    />
                                </th>
                                <th style={{ width: 30 }}>#</th>
                                <th style={{ width: 30 }}></th>
                                <th style={{ minWidth: 110 }}>First Name *</th>
                                <th style={{ minWidth: 110 }}>Last Name *</th>
                                <th style={{ minWidth: 180 }}>Email *</th>
                                <th style={{ minWidth: 110 }}>Phone</th>
                                <th style={{ minWidth: 90 }}>Salary</th>
                                <th style={{ minWidth: 100 }}>Dept</th>
                                <th style={{ minWidth: 120 }}>Designation</th>
                                <th style={{ minWidth: 80 }}>Join Date</th>
                                <th style={{ minWidth: 80 }}>Notes</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row, idx) => (
                                <tr key={idx} style={{ background: rowBg(row), opacity: row.status === 'duplicate' || row.include === false ? 0.75 : 1 }}>
                                    <td className="ps-3">
                                        {row.status !== 'duplicate' && (
                                            <Form.Check
                                                type="checkbox"
                                                checked={row.include !== false}
                                                onChange={() => toggleRow(idx)}
                                            />
                                        )}
                                    </td>
                                    <td className="text-muted">{row.rowNum}</td>
                                    <td>{rowIcon(row)}</td>
                                    <td>
                                        <Form.Control size="sm"
                                            value={row.first_name || ''}
                                            isInvalid={row.errors?.some(e => e.includes('first_name'))}
                                            onChange={e => editField(idx, 'first_name', e.target.value)}
                                            disabled={row.status === 'duplicate'}
                                            style={{ fontSize: 11, padding: '2px 6px' }}
                                        />
                                    </td>
                                    <td>
                                        <Form.Control size="sm"
                                            value={row.last_name || ''}
                                            isInvalid={row.errors?.some(e => e.includes('last_name'))}
                                            onChange={e => editField(idx, 'last_name', e.target.value)}
                                            disabled={row.status === 'duplicate'}
                                            style={{ fontSize: 11, padding: '2px 6px' }}
                                        />
                                    </td>
                                    <td>
                                        <Form.Control size="sm" type="email"
                                            value={row.email || ''}
                                            isInvalid={row.errors?.some(e => e.includes('email'))}
                                            onChange={e => editField(idx, 'email', e.target.value)}
                                            disabled={row.status === 'duplicate'}
                                            style={{ fontSize: 11, padding: '2px 6px' }}
                                        />
                                    </td>
                                    <td>
                                        <Form.Control size="sm"
                                            value={row.phone || ''}
                                            onChange={e => editField(idx, 'phone', e.target.value)}
                                            disabled={row.status === 'duplicate'}
                                            style={{ fontSize: 11, padding: '2px 6px' }}
                                        />
                                    </td>
                                    <td>
                                        <Form.Control size="sm" type="number"
                                            value={row.gross_salary || ''}
                                            onChange={e => editField(idx, 'gross_salary', e.target.value)}
                                            disabled={row.status === 'duplicate'}
                                            style={{ fontSize: 11, padding: '2px 6px', width: 80 }}
                                        />
                                    </td>
                                    <td>
                                        <Form.Control size="sm"
                                            value={row.department_name || ''}
                                            onChange={e => editField(idx, 'department_name', e.target.value)}
                                            disabled={row.status === 'duplicate'}
                                            style={{ fontSize: 11, padding: '2px 6px' }}
                                            title={row.warnings?.find(w => w.includes('Department')) || ''}
                                        />
                                        {row.warnings?.find(w => w.includes('Department')) && (
                                            <div style={{ fontSize: 10, color: '#d97706' }}>⚠ Not found</div>
                                        )}
                                    </td>
                                    <td>
                                        <Form.Control size="sm"
                                            value={row.designation_name || ''}
                                            onChange={e => editField(idx, 'designation_name', e.target.value)}
                                            disabled={row.status === 'duplicate'}
                                            style={{ fontSize: 11, padding: '2px 6px' }}
                                            title={row.warnings?.find(w => w.includes('Designation')) || ''}
                                        />
                                        {row.warnings?.find(w => w.includes('Designation')) && (
                                            <div style={{ fontSize: 10, color: '#d97706' }}>⚠ Not found</div>
                                        )}
                                    </td>
                                    <td>
                                        <Form.Control size="sm" type="date"
                                            value={row.join_date || ''}
                                            onChange={e => editField(idx, 'join_date', e.target.value)}
                                            disabled={row.status === 'duplicate'}
                                            style={{ fontSize: 11, padding: '2px 6px', width: 110 }}
                                        />
                                    </td>
                                    <td style={{ maxWidth: 160 }}>
                                        {row.status === 'duplicate' && (
                                            <span style={{ fontSize: 10, color: '#d97706' }}>Email already exists</span>
                                        )}
                                        {row.errors?.map((e, j) => (
                                            <div key={j} style={{ fontSize: 10, color: '#dc2626' }}>{e}</div>
                                        ))}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                {includedCount === 0 && (
                    <Alert variant="warning" className="mx-4 my-3 small py-2 mb-0">
                        No rows selected for import. Check at least one row to proceed.
                    </Alert>
                )}
                {includedCount > 0 && (
                    <Alert variant="info" className="mx-4 my-3 small py-2 mb-0">
                        <strong>{includedCount} employee{includedCount !== 1 ? 's' : ''}</strong> will be imported.
                        A welcome email with login credentials will be sent to each one.
                    </Alert>
                )}
            </Modal.Body>
            <Modal.Footer className="border-0 pt-0">
                <Button variant="light" onClick={onHide} disabled={importing}>Cancel</Button>
                <Button
                    variant="primary"
                    disabled={importing || includedCount === 0}
                    onClick={() => onConfirm(rows.filter(r => r.include !== false && r.status !== 'duplicate'))}>
                    {importing
                        ? <><Spinner size="sm" className="me-2" />Importing…</>
                        : <><ChevronRight size={14} className="me-1" />Import {includedCount} Employee{includedCount !== 1 ? 's' : ''}</>
                    }
                </Button>
            </Modal.Footer>
        </Modal>
    );
};
// --- Import Result Modal ------------------------------------------------------
const ImportResultModal = ({ show, onHide, result, onRefresh }) => {
    if (!result) return null;
    const { imported = 0, skipped = 0, failed = 0, rows = [] } = result;
    return (
        <Modal show={show} onHide={onHide} centered size="lg">
            <Modal.Header closeButton className="border-0">
                <Modal.Title className="fw-bold">
                    <FileSpreadsheet size={18} className="me-2 text-primary"/>
                    Import Results
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Row className="g-3 mb-4">
                    {[
                        { label:'Imported', count:imported, bg:'#d1fae5', color:'#065f46', border:'#a7f3d0' },
                        { label:'Skipped',  count:skipped,  bg:'#fef3c7', color:'#92400e', border:'#fde68a' },
                        { label:'Failed',   count:failed,   bg:'#fee2e2', color:'#991b1b', border:'#fca5a5' },
                    ].map(({ label, count, bg, color, border }) => (
                        <Col key={label}>
                            <div style={{ background:bg, border:`1px solid ${border}`, borderRadius:12, padding:'16px', textAlign:'center' }}>
                                <div style={{ fontSize:32, fontWeight:800, color }}>{count}</div>
                                <div style={{ fontSize:12, color, fontWeight:600 }}>{label}</div>
                            </div>
                        </Col>
                    ))}
                </Row>
                {rows.length > 0 && (
                    <div style={{ maxHeight:300, overflowY:'auto', borderRadius:8, border:'1px solid #e5e7eb' }}>
                        <table className="table table-sm mb-0 align-middle" style={{ fontSize:12 }}>
                            <thead className="table-light" style={{ position:'sticky', top:0 }}>
                                <tr>
                                    <th className="ps-3">Row</th>
                                    <th>Name / Email</th>
                                    <th>Code</th>
                                    <th>Status</th>
                                    <th>Notes</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((r, i) => (
                                    <tr key={i} style={{
                                        background: r.status==='imported' ? '#f0fdf4' :
                                                    r.status==='skipped'  ? '#fffbeb' : '#fef2f2'
                                    }}>
                                        <td className="ps-3 text-muted">{r.row}</td>
                                        <td>
                                            <div className="fw-bold">{r.name}</div>
                                            {r.email && <div className="text-muted" style={{fontSize:11}}>{r.email}</div>}
                                        </td>
                                        <td><code style={{fontSize:11}}>{r.employee_code || '—'}</code></td>
                                        <td>
                                            <Badge bg={r.status==='imported'?'success':r.status==='skipped'?'warning':'danger'} style={{fontSize:10}}>
                                                {r.status}
                                            </Badge>
                                        </td>
                                        <td style={{maxWidth:200}}>
                                            {r.reason && <div className="text-muted" style={{fontSize:11}}>{r.reason}</div>}
                                            {r.errors?.map((e,j) => <div key={j} className="text-danger" style={{fontSize:11}}>✗ {e}</div>)}
                                            {r.warnings?.map((w,j) => <div key={j} style={{fontSize:11,color:'#d97706'}}>⚠ {w}</div>)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
                {imported > 0 && (
                    <Alert variant="success" className="mt-3 small py-2 mb-0">
                        ✅ {imported} employee{imported!==1?'s':''} imported successfully.
                        A welcome email with login credentials has been sent to each employee.
                    </Alert>
                )}
            </Modal.Body>
            <Modal.Footer className="border-0">
                <Button variant="light" onClick={onHide}>Close</Button>
                {imported > 0 && (
                    <Button variant="success" onClick={() => { onHide(); onRefresh(); }}>
                        View Employees →
                    </Button>
                )}
            </Modal.Footer>
        </Modal>
    );
};
// --- Form Validation ----------------------------------------------------------
const validateForm = (f) => {
    const e = {};
    if (!f.first_name?.trim())  e.first_name = 'First name is required';
    if (!f.last_name?.trim())   e.last_name  = 'Last name is required';
    if (!f.role_id)             e.role_id    = 'Please select a role';
    if (f.email) {
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email)) e.email = 'Enter a valid email address';
    } else { e.email = 'Email address is required'; }
    if (f.phone) {
        const digits = String(f.phone).replace(/\D/g, '');
        if (digits.length > 0 && (digits.length < 10 || digits.length > 12))
            e.phone = 'Enter a valid 10-digit mobile number';
    }
    if (f.gross_salary && parseFloat(f.gross_salary) < 0) e.gross_salary = 'Salary cannot be negative';
    if (f.dob) {
        const today = new Date().toISOString().slice(0, 10);
        if (f.dob > today) e.dob = 'Date of birth cannot be in the future';
        const minYear = new Date().getFullYear() - 100;
        if (parseInt(f.dob.slice(0, 4)) < minYear) e.dob = 'Please enter a valid date of birth';
    }
    if (f.pan_number && !/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(f.pan_number.toUpperCase()))
        e.pan_number = 'Invalid PAN. Format: ABCDE1234F (5 letters + 4 digits + 1 letter)';
    return e;
};
const EMPTY_FORM = {
    first_name: '', last_name: '', email: '', phone: '',
    role_id: '', employee_code: '', gross_salary: '',
    employment_type: 'Full-Time', department_id: '',
    designation_id: '', manager_id: '', shift_id: '',
    join_date: new Date().toISOString().slice(0, 10),
    password: 'Password@123',
    work_state: '', pf_opt_out: 0, income_tax_regime: 'new', dob: '',
    pan_number: '',
};
// --- Add / Edit Employee Modal -------------------------------------------------
const EmployeeFormModal = ({
    show, onHide, onSuccess, editing = null,
    roles = [], allEmployees = [], departments = [], designations = [], shifts = [],
}) => {
    const isEdit = !!editing;
    const [form,   setForm]   = useState(EMPTY_FORM);
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);
    const [autoLoad, setAutoLoad] = useState(false);
    const [companySettings, setCompanySettings] = useState({ code: '', domain: '' });
    const autoEditedRef = useRef({ email: false, employee_code: false });
    useEffect(() => {
        if (!show) return;
        autoEditedRef.current = { email: false, employee_code: false };
        if (isEdit && editing) {
            setForm({
                first_name:        editing.first_name        ?? '',
                last_name:         editing.last_name         ?? '',
                email:             editing.email             ?? '',
                phone:             editing.phone             ?? '',
                role_id:           editing.role_id           ?? '',
                employee_code:     editing.employee_code     ?? '',
                gross_salary:      editing.gross_salary      ?? editing.base_salary ?? '',
                employment_type:   editing.employment_type   ?? 'Full-Time',
                department_id:     editing.department_id     ?? '',
                designation_id:    editing.designation_id    ?? '',
                manager_id:        editing.manager_id        ?? '',
                shift_id:          editing.shift_id          ?? '',
                join_date:         editing.join_date ? String(editing.join_date).slice(0, 10) : '',
                status:            editing.status            ?? 'active',
                work_state:        editing.work_state        ?? '',
                pf_opt_out:        editing.pf_opt_out        ?? 0,
                income_tax_regime: editing.income_tax_regime ?? 'new',
                dob:               editing.dob ? String(editing.dob).slice(0, 10) : '',
                pan_number:        (editing.pan_number       ?? '').toUpperCase(),
            });
        } else { setForm(EMPTY_FORM); }
        setErrors({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show, editing?.id, isEdit]);
    useEffect(() => {
        if (!show || isEdit) return;
        axiosInstance.get('/companies/my').then(res => {
            const co = res.data.data || {};
            setCompanySettings({ code: co.company_code || '', domain: co.company_domain || (co.email?.split('@')[1] || '') });
        }).catch(() => {});
    }, [show, isEdit]);
    const set = (field, value) => {
        setForm(p => ({ ...p, [field]: value }));
        if (errors[field]) setErrors(p => ({ ...p, [field]: '' }));
    };
    const generateFieldsFromName = async (firstName) => {
        if (isEdit || !firstName.trim()) return;
        const name = firstName.trim().toLowerCase().replace(/[^a-z0-9]/g, '');
        if (!name) return;
        setAutoLoad(true);
        try {
            if (!autoEditedRef.current.email && companySettings.domain)
                set('email', `${name}@${companySettings.domain}`);
            if (!autoEditedRef.current.employee_code) {
                try {
                    const res = await axiosInstance.get(`/users/auto-fields?first_name=${encodeURIComponent(firstName.trim())}`);
                    const { employee_code } = res.data.data || {};
                    if (employee_code) set('employee_code', employee_code);
                } catch {}
            }
        } finally { setAutoLoad(false); }
    };
    const handleFirstNameChange = (value) => {
        set('first_name', value);
        if (!isEdit && !autoEditedRef.current.email)         set('email', '');
        if (!isEdit && !autoEditedRef.current.employee_code) set('employee_code', '');
    };
    const handleSubmit = async (ev) => {
        ev.preventDefault();
        const errs = validateForm(form);
        if (Object.keys(errs).length) { setErrors(errs); return; }
        setSaving(true);
        try {
            if (isEdit) {
                await axiosInstance.put(`/employees/${editing.id}`, form);
                toast.success('Employee updated successfully');
            } else {
                await axiosInstance.post('/users', form);
                toast.success(`Employee created! Login: ${form.email} | Password: Password@123`);
            }
            onSuccess(); onHide();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Operation failed');
        } finally { setSaving(false); }
    };
    const managerOptions = allEmployees.filter(e => !isEdit || e.id !== editing?.id);
    const today = new Date().toISOString().slice(0, 10);
    return (
        <Modal show={show} onHide={onHide} centered size="lg">
            <Modal.Header closeButton className="border-0">
                <Modal.Title className="fw-bold">{isEdit ? 'Edit Employee' : 'Add New Employee'}</Modal.Title>
            </Modal.Header>
            <Form noValidate onSubmit={handleSubmit}>
                <Modal.Body>
                    {!isEdit && (
                        <Alert variant="info" className="small py-2 mb-3">
                            Employee code and email are auto-generated from first name.
                            Temporary password: <strong>Password@123</strong>
                        </Alert>
                    )}
                    <p className="small fw-bold text-muted text-uppercase mb-2" style={{ letterSpacing: '0.05em' }}>Basic Information</p>
                    <Row className="g-3 mb-3">
                        <Col xs={12} sm={6}>
                            <Form.Label className="small fw-bold">First Name *</Form.Label>
                            <Form.Control value={form.first_name} placeholder="e.g. Kiran"
                                onChange={e => handleFirstNameChange(e.target.value)}
                                onBlur={e => generateFieldsFromName(e.target.value.trim())}
                                isInvalid={!!errors.first_name}/>
                            <Form.Control.Feedback type="invalid">{errors.first_name}</Form.Control.Feedback>
                        </Col>
                        <Col xs={12} sm={6}>
                            <Form.Label className="small fw-bold">Last Name *</Form.Label>
                            <Form.Control value={form.last_name} placeholder="e.g. Kumar"
                                onChange={e => set('last_name', e.target.value)} isInvalid={!!errors.last_name}/>
                            <Form.Control.Feedback type="invalid">{errors.last_name}</Form.Control.Feedback>
                        </Col>
                        <Col xs={12} sm={6}>
                            <Form.Label className="small fw-bold">
                                Email Address * {autoLoad && <Spinner size="sm" className="ms-2"/>}
                            </Form.Label>
                            <Form.Control type="email"
                                placeholder={autoLoad ? 'Generating…' : companySettings.domain ? `firstname@${companySettings.domain}` : 'e.g. kiran@company.in'}
                                value={form.email}
                                onChange={e => { autoEditedRef.current.email = true; set('email', e.target.value); }}
                                readOnly={isEdit} className={isEdit ? 'bg-light' : ''} isInvalid={!!errors.email}/>
                            <Form.Control.Feedback type="invalid">{errors.email}</Form.Control.Feedback>
                        </Col>
                        <Col xs={12} sm={6}>
                            <Form.Label className="small fw-bold">Phone Number</Form.Label>
                            <InputGroup>
                                <InputGroup.Text><Phone size={15}/></InputGroup.Text>
                                <Form.Control placeholder="9876543210" value={form.phone}
                                    onChange={e => set('phone', e.target.value.replace(/[^\d+]/g,'').slice(0,13))}
                                    isInvalid={!!errors.phone}/>
                                <Form.Control.Feedback type="invalid">{errors.phone}</Form.Control.Feedback>
                            </InputGroup>
                        </Col>
                        <Col xs={12} sm={6}>
                            <Form.Label className="small fw-bold d-flex align-items-center gap-1">
                                <Calendar size={14} className="text-primary"/>Date of Birth
                                <span className="fw-normal text-muted">(optional)</span>
                            </Form.Label>
                            <Form.Control type="date" value={form.dob} max={today} min="1930-01-01"
                                isInvalid={!!errors.dob} onChange={e => set('dob', e.target.value)}/>
                            <Form.Control.Feedback type="invalid">{errors.dob}</Form.Control.Feedback>
                            {form.dob && !errors.dob && (
                                <Form.Text className="text-muted">
                                    {fmtDate(form.dob)}{calcAge(form.dob) !== null && ` · ${calcAge(form.dob)} years old`}
                                </Form.Text>
                            )}
                        </Col>
                        <Col xs={12} sm={6}>
                            <Form.Label className="small fw-bold">System Role *</Form.Label>
                            <Form.Select value={form.role_id} onChange={e => set('role_id', e.target.value)} isInvalid={!!errors.role_id}>
                                <option value="">Select role…</option>
                                {roles.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                            </Form.Select>
                            <Form.Control.Feedback type="invalid">{errors.role_id}</Form.Control.Feedback>
                        </Col>
                        <Col xs={12} sm={6}>
                            <Form.Label className="small fw-bold">
                                Employee Code {autoLoad && <Spinner size="sm" className="ms-2"/>}
                            </Form.Label>
                            <Form.Control placeholder={autoLoad ? 'Auto-generating…' : 'e.g. I2S-012'} value={form.employee_code}
                                onChange={e => { autoEditedRef.current.employee_code = true; set('employee_code', e.target.value); }}/>
                        </Col>
                    </Row>
                    <hr className="my-2"/>
                    <p className="small fw-bold text-muted text-uppercase mb-2" style={{ letterSpacing: '0.05em' }}>Organisation &amp; Reporting</p>
                    <Row className="g-3 mb-3">
                        <Col xs={12} sm={6}>
                            <Form.Label className="small fw-bold">Department</Form.Label>
                            <Form.Select value={form.department_id} onChange={e => set('department_id', e.target.value)}>
                                <option value="">Select department…</option>
                                {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                            </Form.Select>
                        </Col>
                        <Col xs={12} sm={6}>
                            <Form.Label className="small fw-bold">Designation</Form.Label>
                            <Form.Select value={form.designation_id} onChange={e => set('designation_id', e.target.value)}>
                                <option value="">Select designation…</option>
                                {designations.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                            </Form.Select>
                        </Col>
                        <Col xs={12}>
                            <Form.Label className="small fw-bold d-flex align-items-center gap-2">
                                <GitBranch size={14} className="text-primary"/>Reporting Manager
                            </Form.Label>
                            <Form.Select value={form.manager_id} onChange={e => set('manager_id', e.target.value)}>
                                <option value="">No manager — top level</option>
                                {managerOptions.map(e => (
                                    <option key={e.id} value={e.id}>
                                        {e.first_name} {e.last_name}{e.designation_name ? ` — ${e.designation_name}` : ''}
                                    </option>
                                ))}
                            </Form.Select>
                        </Col>
                    </Row>
                    <hr className="my-2"/>
                    <p className="small fw-bold text-muted text-uppercase mb-2" style={{ letterSpacing: '0.05em' }}>Employment Details</p>
                    <Row className="g-3">
                        <Col xs={12} sm={6}>
                            <Form.Label className="small fw-bold">Employment Type</Form.Label>
                            <Form.Select value={form.employment_type} onChange={e => set('employment_type', e.target.value)}>
                                <option>Full-Time</option><option>Part-Time</option>
                                <option>Contract</option><option>Intern</option>
                            </Form.Select>
                        </Col>
                        <Col xs={12} sm={6}>
                            <Form.Label className="small fw-bold d-flex align-items-center gap-1">
                                <Clock size={14} className="text-primary"/>Shift
                            </Form.Label>
                            <Form.Select value={form.shift_id} onChange={e => set('shift_id', e.target.value)}>
                                <option value="">No specific shift</option>
                                {shifts.map(s => (
                                    <option key={s.id} value={s.id}>
                                        {s.name} ({s.start_time?.slice(0,5)} – {s.end_time?.slice(0,5)})
                                    </option>
                                ))}
                            </Form.Select>
                        </Col>
                        <Col xs={12} sm={6}>
                            <Form.Label className="small fw-bold">Join Date</Form.Label>
                            <Form.Control type="date" value={form.join_date} max={today} onChange={e => set('join_date', e.target.value)}/>
                        </Col>
                        <Col xs={12} sm={6}>
                            <Form.Label className="small fw-bold">Gross Salary (₹)</Form.Label>
                            <InputGroup>
                                <InputGroup.Text>₹</InputGroup.Text>
                                <Form.Control type="number" min="0" placeholder="e.g. 50000"
                                    value={form.gross_salary} onChange={e => set('gross_salary', e.target.value)}
                                    isInvalid={!!errors.gross_salary}/>
                                <Form.Control.Feedback type="invalid">{errors.gross_salary}</Form.Control.Feedback>
                            </InputGroup>
                        </Col>
                        <Col xs={12}><hr className="my-1"/>
                            <p className="small fw-bold text-muted text-uppercase mb-2" style={{ letterSpacing: '0.05em' }}>Payroll &amp; Tax Settings</p>
                        </Col>
                        <Col xs={12} sm={6}>
                            <Form.Label className="small fw-bold">Work State <span className="fw-normal text-muted">(for PT)</span></Form.Label>
                            <Form.Select value={form.work_state} onChange={e => set('work_state', e.target.value)}>
                                <option value="">— Use company default state —</option>
                                {INDIAN_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                            </Form.Select>
                        </Col>
                        <Col xs={12} sm={6}>
                            <Form.Label className="small fw-bold">Income Tax Regime</Form.Label>
                            <Form.Select value={form.income_tax_regime} onChange={e => set('income_tax_regime', e.target.value)}>
                                <option value="new">New Regime (Default — FY 2024-25)</option>
                                <option value="old">Old Regime (with exemptions/deductions)</option>
                            </Form.Select>
                        </Col>
                        <Col xs={12} sm={6}>
                            <Form.Label className="small fw-bold d-flex align-items-center gap-1">
                                <Shield size={14} className="text-primary"/>
                                PAN Number
                                <span className="fw-normal text-muted">(for Form 16)</span>
                            </Form.Label>
                            <Form.Control
                                placeholder="ABCDE1234F"
                                value={form.pan_number || ''}
                                maxLength={10}
                                style={{
                                    fontFamily:    'monospace',
                                    textTransform: 'uppercase',
                                    letterSpacing: '1px',
                                }}
                                onChange={e => set('pan_number', e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
                                isInvalid={!!errors.pan_number}
                                isValid={!!(form.pan_number && form.pan_number.length === 10 && !errors.pan_number)}
                            />
                            <Form.Control.Feedback type="invalid">
                                {errors.pan_number}
                            </Form.Control.Feedback>
                            <Form.Text className="text-muted">
                                Required for Form 16. Format: ABCDE1234F
                            </Form.Text>
                        </Col>
                        {parseFloat(form.gross_salary) > 15000 && (
                            <Col xs={12}>
                                <div className="d-flex align-items-start gap-2 p-3 rounded" style={{ background:'#f8fafc', border:'1px solid #e2e8f0' }}>
                                    <Form.Check type="switch" id="pf-opt-out" checked={!!form.pf_opt_out}
                                        onChange={e => set('pf_opt_out', e.target.checked ? 1 : 0)} className="mt-0"/>
                                    <div>
                                        <div className="small fw-bold">PF Opt-out</div>
                                        <div className="text-muted" style={{ fontSize:11 }}>
                                            Salary &gt; ₹15,000 — eligible to opt out.{' '}
                                            {form.pf_opt_out
                                                ? <span className="text-danger">PF will NOT be deducted.</span>
                                                : <span className="text-success">PF will be deducted as per company mode.</span>}
                                        </div>
                                    </div>
                                </div>
                            </Col>
                        )}
                        {isEdit && (
                            <Col xs={12} sm={6}>
                                <Form.Label className="small fw-bold">Status</Form.Label>
                                <Form.Select value={form.status} onChange={e => set('status', e.target.value)}>
                                    <option value="active">Active</option>
                                    <option value="inactive">On Hold</option>
                                </Form.Select>
                            </Col>
                        )}
                    </Row>
                </Modal.Body>
                <Modal.Footer className="border-0">
                    <Button variant="light" type="button" onClick={onHide} disabled={saving}>Cancel</Button>
                    <Button variant="primary" type="submit" disabled={saving}>
                        {saving && <Spinner size="sm" className="me-1"/>}
                        {isEdit ? 'Save Changes' : 'Create Account'}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};
// --- Main Component ------------------------------------------------------------
const EmployeeList = ({ myTeamOnly = false }) => {
    const { user }       = useAuth();
    const isAdmin        = ADMIN_ROLES.includes(user?.role) && !myTeamOnly;
    const [employees,    setEmployees]    = useState([]);
    const [roles,        setRoles]        = useState([]);
    const [departments,  setDepartments]  = useState([]);
    const [designations, setDesignations] = useState([]);
    const [shifts,       setShifts]       = useState([]);
    const [loading,      setLoading]      = useState(true);
    const [searchTerm,   setSearch]       = useState('');
    const [planLimit,    setPlanLimit]    = useState(null);
    const [planName,     setPlanName]     = useState('');
    const [viewModal,    setViewModal]    = useState({ show: false, emp: null });
    const [editModal,    setEditModal]    = useState({ show: false, emp: null });
    const [addModal,     setAddModal]     = useState(false);
    const [deleteModal,  setDeleteModal]  = useState({ show: false, emp: null, loading: false });
    const [holdModal,    setHoldModal]    = useState({ show: false, emp: null, loading: false });
    const [letterModal,  setLetterModal]  = useState({ show: false, emp: null }); // FEATURE 10
    const [previewing,        setPreviewing]        = useState(false);
    const [previewData,       setPreviewData]       = useState(null);
    const [showPreview,       setShowPreview]       = useState(false);
    const [importing,         setImporting]         = useState(false);
    const [importResult,      setImportResult]      = useState(null);
    const [showImportResult,  setShowImportResult]  = useState(false);
    const fileInputRef = useRef(null);
    const fetchEmployees = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axiosInstance.get('/employees');
            const d = res.data.data;
            setEmployees(Array.isArray(d) ? d : (d?.employees || []));
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to load employees');
            setEmployees([]);
        } finally { setLoading(false); }
    }, []);
    const fetchPlanLimit = useCallback(async () => {
        try {
            const res = await axiosInstance.get('/companies/my');
            const co  = res.data.data || {};
            setPlanLimit(co.max_employees ?? null);
            setPlanName(co.plan_name || co.plan_code || '');
        } catch {}
    }, []);
    const fetchSupport = useCallback(async () => {
        try {
            const [rolesRes, deptsRes, desigRes, shiftsRes] = await Promise.all([
                axiosInstance.get('/roles'),
                axiosInstance.get('/hr/departments').catch(() => ({ data: { data: [] } })),
                axiosInstance.get('/hr/designations').catch(() => ({ data: { data: [] } })),
                axiosInstance.get('/shifts').catch(() => ({ data: { data: [] } })),
            ]);
            setRoles(rolesRes.data.data || []);
            setDepartments(deptsRes.data.data || []);
            setDesignations(desigRes.data.data || []);
            setShifts(shiftsRes.data.data || []);
        } catch {}
    }, []);
    useEffect(() => {
        fetchEmployees();
        fetchSupport();
        fetchPlanLimit();
    }, [fetchEmployees, fetchSupport, fetchPlanLimit]);
    const openViewModal = useCallback(async (emp) => {
        setViewModal({ show: true, emp });
        try {
            const res  = await axiosInstance.get(`/employees/${emp.id}`);
            const full = res.data.data || res.data || {};
            setViewModal({ show: true, emp: { ...emp, ...full } });
        } catch {}
    }, []);
    const openEditModal = useCallback(async (emp) => {
        setEditModal({ show: true, emp });
        try {
            const res  = await axiosInstance.get(`/employees/${emp.id}`);
            const full = res.data.data || res.data || {};
            setEditModal({ show: true, emp: { ...emp, ...full } });
        } catch {}
    }, []);
    const downloadTemplate = () => {
        axiosInstance.get('/employees/bulk-import/template', { responseType: 'blob' })
            .then(res => {
                const url  = window.URL.createObjectURL(new Blob([res.data]));
                const link = document.createElement('a');
                link.href  = url;
                link.download = 'PeoplesDock_Employee_Import_Template.csv';
                link.click();
                window.URL.revokeObjectURL(url);
            })
            .catch(() => toast.error('Failed to download template'));
    };
    const handleFileSelected = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        e.target.value = '';
        const ext = file.name.split('.').pop().toLowerCase();
        if (!['csv', 'xlsx', 'xls'].includes(ext)) {
            toast.error('Please upload a .csv or .xlsx file');
            return;
        }
        if (file.size > 10 * 1024 * 1024) {
            toast.error('File too large. Maximum 10 MB allowed.');
            return;
        }
        setPreviewing(true);
        try {
            const formData = new FormData();
            formData.append('file', file);
            const res = await axiosInstance.post('/employees/bulk-import/preview', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            const data = res.data.data;
            if (!data?.rows?.length) {
                toast.error('No data rows found in the file.');
                return;
            }
            setPreviewData(data);
            setShowPreview(true);
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to parse file. Check the format and try again.');
        } finally {
            setPreviewing(false);
        }
    };
    const handleConfirmImport = async (confirmedRows) => {
        if (!confirmedRows.length) { toast.warning('No rows selected.'); return; }
        setImporting(true);
        try {
            const res = await axiosInstance.post('/employees/bulk-import', {
                rows: confirmedRows,
            }, {
                headers: { 'Content-Type': 'application/json' },
            });
            const result = res.data.data;
            setImportResult(result);
            setShowPreview(false);
            setPreviewData(null);
            setShowImportResult(true);
            if (result.imported > 0) fetchEmployees();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Import failed. Please try again.');
        } finally {
            setImporting(false);
        }
    };
    const activeEmployeeCount = employees.filter(e => e.status === 'active').length;
    const isLimitUnlimited    = !planLimit || planLimit >= 9999;
    const isAtLimit           = !isLimitUnlimited && activeEmployeeCount >= planLimit;
    const confirmDelete = async () => {
        setDeleteModal(p => ({ ...p, loading: true }));
        try {
            await axiosInstance.delete(`/employees/${deleteModal.emp.id}`);
            toast.success(`${deleteModal.emp.first_name} has been removed`);
            setDeleteModal({ show: false, emp: null, loading: false });
            fetchEmployees();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Delete failed');
            setDeleteModal(p => ({ ...p, loading: false }));
        }
    };
    const confirmHold = async () => {
        const newStatus = holdModal.emp.status === 'active' ? 'inactive' : 'active';
        setHoldModal(p => ({ ...p, loading: true }));
        try {
            await axiosInstance.patch(`/employees/${holdModal.emp.id}/status`, { status: newStatus });
            toast.success(`${holdModal.emp.first_name} ${newStatus === 'active' ? 'activated' : 'put on hold'}`);
            setHoldModal({ show: false, emp: null, loading: false });
            fetchEmployees();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Status update failed');
            setHoldModal(p => ({ ...p, loading: false }));
        }
    };
    const downloadPayslip = async (empId, name) => {
        try {
            const res = await axiosInstance.get(`/payroll/download-latest/${empId}`, { responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const a   = document.createElement('a');
            a.href    = url;
            a.download = `Payslip_${(name || empId).replace(/\s/g, '_')}.html`;
            a.click();
        } catch { toast.error('No payslip found for this employee.'); }
    };
    const handleAddClick = () => {
        if (isAtLimit) {
            toast.warning(`Your ${planName} plan allows up to ${planLimit} active employees. Please upgrade to add more.`);
            return;
        }
        setAddModal(true);
    };
    const safeList = Array.isArray(employees) ? employees : [];
    const filtered = safeList.filter(emp => {
        if (myTeamOnly && emp.manager_id !== user?.id) return false;
        return `${emp.first_name} ${emp.last_name} ${emp.employee_code || ''} ${emp.department_name || ''}`
            .toLowerCase().includes(searchTerm.toLowerCase());
    });
    return (
        <div className="employee-page p-2 p-md-4">
            <input ref={fileInputRef} type="file" accept=".csv,.xlsx,.xls"
                style={{ display:'none' }} onChange={handleFileSelected}/>
            <Row className="mb-4 align-items-center">
                <Col>
                    <h4 className="fw-bold mb-1">{myTeamOnly ? 'My Team' : 'Employee Directory'}</h4>
                    <p className="text-muted small mb-0">
                        {myTeamOnly
                            ? `${filtered.length} direct report${filtered.length !== 1 ? 's' : ''}`
                            : `${safeList.length} total employees`
                              + (!isLimitUnlimited ? ` · Plan limit: ${activeEmployeeCount}/${planLimit} active` : '')}
                    </p>
                </Col>
                <Col xs="auto" className="d-flex gap-2 flex-wrap justify-content-end">
                    {isAdmin && (
                        <>
                            <Button variant="outline-secondary" size="sm"
                                onClick={downloadTemplate} title="Download CSV template">
                                <FileSpreadsheet size={14} className="me-1"/>Template
                            </Button>
                            <Button variant="outline-primary" size="sm"
                                disabled={previewing}
                                onClick={() => fileInputRef.current?.click()}
                                title="Upload CSV or Excel to preview before importing">
                                {previewing
                                    ? <><Spinner size="sm" className="me-1"/>Reading file…</>
                                    : <><Upload size={14} className="me-1"/>Import CSV</>}
                            </Button>
                        </>
                    )}
                    {isAdmin && (
                        <Button
                            variant={isAtLimit ? 'outline-warning' : 'primary'}
                            className="d-flex align-items-center gap-2"
                            onClick={handleAddClick}>
                            {isAtLimit ? <Lock size={16}/> : <UserPlus size={18}/>}
                            <span className="d-none d-sm-inline">
                                {isAtLimit ? 'Plan Limit Reached' : 'Add Employee'}
                            </span>
                        </Button>
                    )}
                </Col>
            </Row>
            {isAdmin && isAtLimit && (
                <Alert variant="warning" className="d-flex align-items-center gap-3 mb-3 py-2">
                    <Lock size={18} className="flex-shrink-0 text-warning"/>
                    <div className="small">
                        <strong>Employee limit reached</strong> — Your <strong>{planName}</strong> plan
                        allows up to <strong>{planLimit}</strong> active employees.{' '}
                        <a href="/billing" className="fw-bold">Upgrade your plan →</a>
                    </div>
                </Alert>
            )}
            {isAdmin && !isAtLimit && !isLimitUnlimited && planLimit &&
             activeEmployeeCount >= Math.floor(planLimit * 0.8) && (
                <Alert variant="info" className="d-flex align-items-center gap-3 mb-3 py-2">
                    <div className="small">
                        <strong>Approaching plan limit</strong> — {activeEmployeeCount} of {planLimit} used.{' '}
                        <a href="/billing" className="fw-bold">View plans →</a>
                    </div>
                </Alert>
            )}
            <Card className="border-0 shadow-sm mb-3">
                <Card.Body className="py-3">
                    <InputGroup style={{ maxWidth: 380 }}>
                        <InputGroup.Text className="bg-light border-0">
                            <Search size={16} className="text-muted"/>
                        </InputGroup.Text>
                        <Form.Control className="bg-light border-0"
                            placeholder="Search by name, code or department…"
                            value={searchTerm} onChange={e => setSearch(e.target.value)}/>
                    </InputGroup>
                </Card.Body>
            </Card>
            <Card className="border-0 shadow-sm">
                {loading ? (
                    <div className="text-center py-5">
                        <Spinner animation="border" variant="primary"/>
                        <p className="mt-2 text-muted small">Loading employees…</p>
                    </div>
                ) : (
                    <Table hover responsive className="align-middle mb-0 small">
                        <thead className="bg-light">
                            <tr className="text-muted text-uppercase" style={{ fontSize: 11 }}>
                                <th className="ps-4">Employee</th>
                                <th>Code</th>
                                <th>Department</th>
                                <th>Shift</th>
                                <th>Manager</th>
                                <th>Status</th>
                                <th className="text-end pe-4">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.length > 0 ? filtered.map(emp => (
                                <tr key={emp.id}>
                                    <td className="ps-4">
                                        <div className="d-flex align-items-center gap-2">
                                            <div style={{ width:36, height:36, borderRadius:'50%', flexShrink:0, background:'#eef2ff', color:'#4f46e5', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:700, fontSize:14 }}>
                                                {emp.first_name?.[0]}{emp.last_name?.[0]}
                                            </div>
                                            <div>
                                                <div className="fw-bold">{emp.first_name} {emp.last_name}</div>
                                                <div className="text-muted" style={{ fontSize:11 }}>
                                                    {emp.designation_name || emp.role_name || 'Staff'}
                                                </div>
                                            </div>
                                        </div>
                                    </td>
                                    <td><code>{emp.employee_code || `PD-${emp.id}`}</code></td>
                                    <td>{emp.department_name || <span className="text-muted">—</span>}</td>
                                    <td>{emp.shift_name      || <span className="text-muted">—</span>}</td>
                                    <td>{emp.manager_name    || <span className="text-muted">—</span>}</td>
                                    <td>
                                        <Badge bg={emp.status === 'active' ? 'success' : 'warning'}
                                            className="text-capitalize px-2 py-1">
                                            {emp.status === 'active' ? 'Active' : 'On Hold'}
                                        </Badge>
                                    </td>
                                    <td className="text-end pe-4">
                                        <div className="d-flex justify-content-end gap-1">
                                            <Button size="sm" variant="outline-secondary" className="py-1" title="View profile"
                                                onClick={() => openViewModal(emp)}>
                                                <Eye size={14}/>
                                            </Button>
                                            <Button size="sm" variant="outline-primary" className="py-1" title="Download Payslip"
                                                onClick={() => downloadPayslip(emp.id, `${emp.first_name}_${emp.last_name}`)}>
                                                <Download size={14}/>
                                            </Button>
                                            {isAdmin && (<>
                                                {/* FEATURE 10: Generate Letter button */}
                                                <Button size="sm" variant="outline-info" className="py-1" title="Generate Letter"
                                                    onClick={() => setLetterModal({ show: true, emp })}>
                                                    <FileSignature size={14}/>
                                                </Button>
                                                <Button size="sm" variant="outline-secondary" className="py-1" title="Edit"
                                                    onClick={() => openEditModal(emp)}>
                                                    <Edit2 size={14}/>
                                                </Button>
                                                <Button size="sm"
                                                    variant={emp.status === 'active' ? 'outline-warning' : 'outline-success'}
                                                    className="py-1"
                                                    title={emp.status === 'active' ? 'Put on Hold' : 'Activate'}
                                                    onClick={() => setHoldModal({ show: true, emp, loading: false })}>
                                                    {emp.status === 'active' ? <PauseCircle size={14}/> : <PlayCircle size={14}/>}
                                                </Button>
                                                <Button size="sm" variant="outline-danger" className="py-1" title="Remove"
                                                    onClick={() => setDeleteModal({ show: true, emp, loading: false })}>
                                                    <Trash2 size={14}/>
                                                </Button>
                                            </>)}
                                        </div>
                                    </td>
                                </tr>
                            )) : (
                                <tr>
                                    <td colSpan="7" className="text-center py-5 text-muted">
                                        {searchTerm ? `No employees matching "${searchTerm}"` : 'No employees found.'}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </Table>
                )}
            </Card>
            {/* -- Modals -- */}
            <ViewModal show={viewModal.show} onHide={() => setViewModal({ show:false, emp:null })} emp={viewModal.emp}/>
            <EmployeeFormModal
                show={addModal} onHide={() => setAddModal(false)} onSuccess={fetchEmployees}
                roles={roles} allEmployees={safeList} departments={departments}
                designations={designations} shifts={shifts}/>
            <EmployeeFormModal
                show={editModal.show} onHide={() => setEditModal({ show:false, emp:null })}
                onSuccess={fetchEmployees} editing={editModal.emp}
                roles={roles} allEmployees={safeList} departments={departments}
                designations={designations} shifts={shifts}/>
            {/* FEATURE 10: Generate Letter modal */}
            <GenerateLetterModal
                show={letterModal.show}
                onHide={() => setLetterModal({ show:false, emp:null })}
                employee={letterModal.emp}/>
            <ImportPreviewModal
                show={showPreview}
                onHide={() => { setShowPreview(false); setPreviewData(null); }}
                previewData={previewData}
                onConfirm={handleConfirmImport}
                importing={importing}
            />
            <ImportResultModal
                show={showImportResult}
                onHide={() => setShowImportResult(false)}
                result={importResult}
                onRefresh={fetchEmployees}
            />
            <ConfirmModal
                show={deleteModal.show}
                onHide={() => setDeleteModal({ show:false, emp:null, loading:false })}
                onConfirm={confirmDelete} loading={deleteModal.loading}
                title="Remove Employee"
                message={`Are you sure you want to remove ${deleteModal.emp?.first_name} ${deleteModal.emp?.last_name}?`}
                confirmLabel="Yes, Remove" variant="danger"/>
            <ConfirmModal
                show={holdModal.show}
                onHide={() => setHoldModal({ show:false, emp:null, loading:false })}
                onConfirm={confirmHold} loading={holdModal.loading}
                title={holdModal.emp?.status === 'active' ? 'Put Employee on Hold' : 'Activate Employee'}
                message={holdModal.emp?.status === 'active'
                    ? `Put ${holdModal.emp?.first_name} on hold? They will lose system access.`
                    : `Reactivate ${holdModal.emp?.first_name}? They will regain access.`}
                confirmLabel={holdModal.emp?.status === 'active' ? 'Yes, Hold' : 'Yes, Activate'}
                variant={holdModal.emp?.status === 'active' ? 'warning' : 'success'}/>
        </div>
    );
};
export default EmployeeList;
